import type { RecordRow } from './api';

// A9 — WHAT KIND OF JOB A ROLE IS, and the one place the answer lives.
//
// ===================================================================
// WHY THIS IS A MODULE AND NOT AN `if` IN EACH SCREEN
// ===================================================================
// Decision 3e76a155: one product, one spine; crew is work without an
// audition; discipline lives on the role. That is a single fact with about
// six consequences on screen — which word goes over the name, whether a sides
// trail can ever arrive, whether an audition is a missing thing or a
// meaningless one — and each consequence read from `role.fields.discipline`
// separately is six places to be wrong.
//
// The day a third discipline exists (background is arguably one; a stunt
// performer is genuinely both), ONE file changes. No screen in this app tests
// `discipline === 'crew'` itself, and a review that finds one has found a
// defect.
//
// ===================================================================
// ⚑ THE DEFAULT IS LOAD-BEARING, AND IT IS APPLIED HERE TOO
// ===================================================================
// The pack declares `default: 'performance'`, and recordWrite applies it —
// but only on WRITE. Every role created before A9 has no `discipline` key at
// all, and those rows are the entire existing product. So the absent case is
// not an edge: it is the common one, and it must mean performance rather than
// unknown.
//
// Anything unrecognised also reads as performance. A pack that grows a value
// this build has not heard of must not silently turn somebody's acting credit
// into a crew job.

export type Discipline = 'performance' | 'crew';

/** What kind of work this role is. Absent, malformed and unrecognised all
 * mean performance — see the header. */
export function disciplineOf(role: Pick<RecordRow, 'fields'> | null | undefined): Discipline {
  const raw = role?.fields?.discipline;
  return raw === 'crew' ? 'crew' : 'performance';
}

/** The word a screen puts over `character_name`.
 *
 * The FIELD is called character_name and always will be — D12 is
 * additive-only and a shipped field cannot be renamed. This is the product's
 * word for it, which is a different thing and is allowed to be right. */
export function roleNoun(role: Pick<RecordRow, 'fields'> | null | undefined): string {
  return disciplineOf(role) === 'crew' ? 'Job' : 'Character';
}

/** Human department, or null. Null both when the role is a performance role
 * and when a crew role has not said — a department nobody entered is not
 * "Other", it is nothing, and inventing one would put a wrong word on a
 * resume. */
export function departmentLabel(role: Pick<RecordRow, 'fields'> | null | undefined): string | null {
  if (disciplineOf(role) !== 'crew') return null;
  const raw = role?.fields?.department;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  return humanizeDepartment(raw);
}

/** `hair_makeup` → `Hair & make-up`. The two-word ones are named rather than
 * title-cased, because "Hair Makeup" is not a department anybody has. */
export function humanizeDepartment(raw: string): string {
  const named: Record<string, string> = {
    hair_makeup: 'Hair & make-up',
    post: 'Post-production',
    script: 'Script & continuity',
  };
  if (named[raw]) return named[raw]!;
  return raw.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Does a read stand between this role and the job?
 *
 * THIS IS THE SEAM. A crew job arrives by conversation, a recommendation or a
 * call — not by an audition — so an audition is not a MISSING thing on a crew
 * role, it is a meaningless one. Every screen that would otherwise render an
 * empty audition region asks this first. */
export function expectsAudition(role: Pick<RecordRow, 'fields'> | null | undefined): boolean {
  return disciplineOf(role) === 'performance';
}

/** Will pages ever be issued for this role?
 *
 * Deliberately a SEPARATE question from expectsAudition even though the two
 * answers are identical today. They will diverge: a stunt performer gets
 * sides and does not always read, and a self-taped commercial books off a
 * tape with no sides at all. Collapsing them now would mean untangling one
 * call site from the other later, in a screen, under a deadline. */
export function expectsSides(role: Pick<RecordRow, 'fields'> | null | undefined): boolean {
  return disciplineOf(role) === 'performance';
}
